import { JobSeekingStatus } from "@nimble/core-client/types";
import { z } from "zod";

import type { formMode } from "@/components/auth/form-mode";
import type { AuthAudience } from "@/lib/constants/auth-audiences";
import type { AuthMethod } from "@/lib/constants/auth-methods";
import {
  CTA_NAMES,
  FORM_NAMES,
  PAGE_EVENTS,
  PROFILE_EVENTS,
} from "@/lib/posthog/events";
import {
  createNpsEligibilityEngine,
  JOB_THRESHOLD_REACHED_KEY,
  JOB_VIEW_THRESHOLD,
  SetPersonFn,
  StorageService,
  VIEWED_JOBS_KEY,
} from "@/lib/posthog/nps-eligibility";
import { wasHiringOutcomeSurveySeen } from "@/lib/posthog/surveys";

export type CaptureEventFn = (
  event: string,
  props?: Record<string, unknown>,
) => void;

const stringArraySchema = z
  .string()
  .optional()
  .nullable()
  .transform((val) => {
    if (!val) return [];
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed as string[];
      return [];
    } catch {
      return [];
    }
  })
  .pipe(z.array(z.string()).catch([]));

export const createTrackingEngine = (
  storage: StorageService,
  captureEventFn: CaptureEventFn,
  setPersonFn: SetPersonFn,
) => {
  const npsEngine = createNpsEligibilityEngine(storage, setPersonFn);

  return {
    trackExpressInterestSubmitted: (props: {
      school_nces_id: string;
      school_name: string;
    }) => {
      npsEngine.registerEngagement();
      captureEventFn(PAGE_EVENTS.FORM_SUBMITTED, {
        form_name: FORM_NAMES.EXPRESS_INTEREST,
        ...props,
      });
    },

    trackMatchAccepted: (props: {
      school_nces_id: string;
      school_name: string;
      source: "recommendation" | "invitation";
    }) => {
      npsEngine.registerEngagement();
      captureEventFn(PAGE_EVENTS.FORM_SUBMITTED, {
        form_name: FORM_NAMES.MATCH_ACCEPTED,
        ...props,
      });
    },

    trackAuthSubmitted: (props: {
      auth_method: AuthMethod;
      shared_device: boolean;
      mode: formMode;
      audience: AuthAudience;
    }) => {
      captureEventFn(PAGE_EVENTS.FORM_SUBMITTED, {
        form_name: FORM_NAMES.AUTH,
        ...props,
      });
    },

    trackJobViewed: (props: {
      job_id: string;
      school_nces_id: string;
      district_id: string;
      state: string;
    }) => {
      captureEventFn(PAGE_EVENTS.PAGE_VIEWED, {
        page_name: "job_details",
        ...props,
      });

      const rawIds = storage.get(VIEWED_JOBS_KEY);
      const viewedJobIds = stringArraySchema.parse(rawIds);

      if (!viewedJobIds.includes(props.job_id)) {
        viewedJobIds.push(props.job_id);
        storage.set(VIEWED_JOBS_KEY, JSON.stringify(viewedJobIds));
      }

      const thresholdAlreadyReached =
        storage.get(JOB_THRESHOLD_REACHED_KEY) === "true";

      if (
        !thresholdAlreadyReached &&
        viewedJobIds.length >= JOB_VIEW_THRESHOLD
      ) {
        npsEngine.registerEngagement();
        captureEventFn(PROFILE_EVENTS.NPS_JOB_VIEWS_THRESHOLD_REACHED, {
          unique_jobs_viewed: viewedJobIds.length,
        });
        storage.set(JOB_THRESHOLD_REACHED_KEY, "true");
      }
    },

    trackQuickApplyClicked: (props: { job_id: string }) => {
      npsEngine.registerEngagement();
      captureEventFn(PAGE_EVENTS.CTA_CLICKED, {
        cta_name: CTA_NAMES.QUICK_APPLY,
        ...props,
      });
    },

    trackExternalApplyClicked: (props: { job_id: string; job_url: string }) => {
      npsEngine.registerEngagement();
      captureEventFn(PAGE_EVENTS.CTA_CLICKED, {
        cta_name: CTA_NAMES.EXTERNAL_APPLY,
        ...props,
      });
    },

    trackSalaryScheduleClicked: (props: {
      school_nces_id: string;
      salary_schedule_url: string;
    }) => {
      npsEngine.registerEngagement();
      captureEventFn(PAGE_EVENTS.CTA_CLICKED, {
        cta_name: CTA_NAMES.SALARY_SCHEDULE,
        ...props,
      });
    },

    trackJobSeekingStatusChanged: (props: {
      previous_job_seeking_status: JobSeekingStatus | null;
      new_job_seeking_status: JobSeekingStatus;
    }) => {
      if (props.previous_job_seeking_status === props.new_job_seeking_status) {
        return;
      }
      captureEventFn(PROFILE_EVENTS.JOB_SEEKING_STATUS_CHANGED, {
        ...props,
        survey_seen_this_session: wasHiringOutcomeSurveySeen(),
      });
    },
  };
};

export type MatchAcceptedSource = "recommendation" | "invitation";
